import { useCallback, useLayoutEffect } from 'react';

import { InternalAxiosRequestConfig } from 'axios';

import api from '@/services';
import { APP_CONFIG_CONSTANTS } from '../../constants/appConfig.constants';

export const useApiInterceptors = (
  apiKey: string,
  token: string | null,
  refreshToken: () => Promise<void>,
  isAuthenticated: boolean,
  onApiKeyError: () => void
) => {
  const requestInterceptor = useCallback(
    async (config: InternalAxiosRequestConfig<any> & { _retry?: boolean }) => {
      if (!config._retry && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (!config.headers['x-api-key'] && apiKey) {
        config.headers['x-api-key'] = apiKey;
      }

      config.timeout = config.timeout || APP_CONFIG_CONSTANTS.REQUEST_TIMEOUT;
      return config;
    },
    [apiKey, token]
  );

  const responseInterceptor = useCallback(
    async (error: any) => {
      const originalRequest =
        error.config as InternalAxiosRequestConfig<any> & { _retry?: boolean };

      // Handle token refresh for authenticated users
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        !originalRequest._retry &&
        isAuthenticated
      ) {
        originalRequest._retry = true;
        try {
          await refreshToken();
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // Handle API key errors
      if (
        error.response?.status === 401 &&
        error.response?.data?.message?.includes('API key')
      ) {
        onApiKeyError();
      }

      return Promise.reject(error);
    },
    [refreshToken, isAuthenticated, onApiKeyError]
  );

  useLayoutEffect(() => {
    const reqInterceptor = api.interceptors.request.use(
      requestInterceptor,
      (error) => Promise.reject(error)
    );
    const resInterceptor = api.interceptors.response.use(
      (response) => response,
      responseInterceptor
    );

    return () => {
      api.interceptors.request.eject(reqInterceptor);
      api.interceptors.response.eject(resInterceptor);
    };
  }, [requestInterceptor, responseInterceptor]);
};
